import type { OwnerTodoItem, OwnerTodoKind } from "@teamna/shared";
import { useState, type ReactNode } from "react";
import { track } from "@teamna/web-client/analytics/web.ts";
import { useOwnerTodo } from "@teamna/web-client/hooks/use-families.ts";
import { Button, CheckIcon, InlineError } from "../../components/index.ts";
import type { Words } from "../families/model.ts";
import { CoverApprovalsPanel } from "../../covers/cover-requests.tsx";
import { FeesToConfirmPanel } from "../../fees/fees-panel.tsx";
import { CameDuringPauseList } from "../came-during-pause.tsx";
import { jobsText, todoText } from "./model.ts";
import {
  AnswersPanel,
  ConfirmParticipantsPanel,
  LinkParentsPanel,
  PendingMembersPanel,
  PlanMonthPanel,
  SessionsPanel,
} from "./panels.tsx";

/**
 * The owner's To do list on Home (concept D, strategy repo `teamna/prototypes/owner-answers.html`,
 * 2026-10-16): every job that waits for the owner in one card, under the greeting and above the
 * numbers. Before it, each job was its own card under the numbers, and an owner on a laptop did not
 * see them without a scroll.
 *
 * The API sends the jobs in order (`packages/core/src/services/owner-todo.ts`). Each row opens in
 * place; one row is open at a time. Most rows do the whole job there. "No parent yet" shows the bar
 * and opens New participants, where the linking tools are. A job leaves the list when nothing
 * waits in it. The list is generic: a new kind needs a source on the API and an entry in `PANELS`.
 */

/**
 * `hold`: a panel hands over a view that must stay after its row leaves the list (the payment link
 * after Send on a fee: the write takes the job off the list, and the link must still show).
 */
type PanelProps = { businessId: string; timezone: string; item: OwnerTodoItem; hold: (view: ReactNode | null) => void };

const PANELS: Record<OwnerTodoKind, (props: PanelProps) => ReactNode> = {
  cover_requests: ({ businessId, timezone }) => <CoverApprovalsPanel businessId={businessId} timezone={timezone} />,
  parent_answers: ({ businessId }) => <AnswersPanel businessId={businessId} />,
  pending_members: ({ businessId }) => <PendingMembersPanel businessId={businessId} />,
  sessions_to_decide: ({ businessId, timezone }) => <SessionsPanel businessId={businessId} timezone={timezone} />,
  participants_to_confirm: ({ businessId }) => <ConfirmParticipantsPanel businessId={businessId} />,
  link_parents: ({ businessId }) => <LinkParentsPanel businessId={businessId} />,
  plan_next_month: ({ item }) => <PlanMonthPanel month={item.month ?? null} />,
  fees_to_confirm: ({ businessId, timezone, hold }) => <FeesToConfirmPanel businessId={businessId} timezone={timezone} hold={hold} />,
  came_during_pause: ({ businessId, timezone }) => <CameDuringPauseList businessId={businessId} timezone={timezone} />,
};

export function TodoCard({
  businessId,
  timezone,
  words,
  now,
}: {
  businessId: string;
  timezone: string;
  words: Words;
  now: Date;
}) {
  const todo = useOwnerTodo(businessId);
  const [open, setOpen] = useState<OwnerTodoKind | null>(null);
  const [held, setHeld] = useState<ReactNode | null>(null);
  const items = todo.data?.items;

  // A row that leaves the list closes, so a job of the same kind that comes back later does not
  // open by itself (review F2.4). Set during render, React's way to adjust state to new data. Home
  // mounts this card with `key={businessId}`, so another business starts with every row closed.
  if (open && items && !items.some((item) => item.kind === open)) setOpen(null);

  if (!items) {
    // Nothing while the first read runs: the card comes in once, with its rows.
    if (todo.isLoading) return null;
    return (
      <section aria-label="To do" className="rounded-card border border-line bg-paper px-5 py-4">
        <InlineError error={todo.error} />
        <Button size="sm" variant="secondary" className="mt-2" onClick={() => void todo.refetch()}>
          Try again
        </Button>
      </section>
    );
  }
  // A read after a good one failed: the last list stays, with a way to read it again (review F2.3).
  const stale = todo.isError;
  if (items.length === 0) {
    return (
      <>
      {held}
      <section aria-label="To do" className="flex items-center gap-3 rounded-card border border-line bg-paper px-5 py-3.5 text-ink-2">
        {/* The check only when the empty list is current (review F4.1). */}
        {stale ? null : (
          <span className="flex size-7 items-center justify-center rounded-full bg-confirmed-surface text-confirmed">
            <CheckIcon size={15} />
          </span>
        )}
        {stale ? (
          // An empty list from an older read: it can be out of date, so it does not say "nothing" (review F3.1).
          <p className="flex flex-wrap items-center gap-2 text-[14.5px]">
            <b className="text-ink">To do:</b> not up to date.
            <Button size="sm" variant="ghost" onClick={() => void todo.refetch()}>
              Try again
            </Button>
          </p>
        ) : (
          <p className="text-[14.5px]">
            <b className="text-ink">To do:</b> nothing waits for you.
          </p>
        )}
      </section>
      </>
    );
  }

  const jobCount = items.length;
  function toggle(item: OwnerTodoItem, position: number) {
    if (open === item.kind) return setOpen(null);
    setOpen(item.kind);
    track("todo_row_opened", { kind: item.kind, count: item.count, position, job_count: jobCount });
  }

  return (
    <>
    {held}
    <section aria-labelledby="todo-title" className="overflow-hidden rounded-card border border-line bg-paper shadow-card">
      <div className="flex items-baseline justify-between gap-3 px-5 pt-4 pb-3">
        <h2 id="todo-title" className="text-[19px]">
          To do
        </h2>
        <span className="flex items-center gap-3 text-[13.5px] font-semibold text-ink-2">
          {stale ? (
            <span className="flex items-center gap-2 font-normal">
              Not up to date.
              <Button size="sm" variant="ghost" onClick={() => void todo.refetch()}>
                Try again
              </Button>
            </span>
          ) : null}
          {jobsText(items.length)}
        </span>
      </div>
      <ol aria-labelledby="todo-title">
        {items.map((item, index) => {
          const text = todoText(item, words, now, timezone);
          const isOpen = open === item.kind;
          const titleId = `todo-${item.kind}-title`;
          const panelId = `todo-${item.kind}-panel`;
          return (
            <li key={item.kind} className="border-t border-line">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5">
                <span
                  aria-hidden="true"
                  className={`size-2.5 shrink-0 rounded-full ${index === 0 ? "bg-accent" : "bg-line-strong"}`}
                />
                <div className="min-w-0 flex-1">
                  <h3 id={titleId} className="text-[15px] font-semibold text-ink">
                    <span dir="auto">{text.title}</span>
                  </h3>
                  <p className="text-[13.5px] text-ink-2 [overflow-wrap:anywhere]">
                    <bdi>{text.detail}</bdi>
                  </p>
                </div>
                <Button
                  size="sm"
                  variant={isOpen ? "ghost" : index === 0 ? "primary" : "secondary"}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  aria-label={isOpen ? `Close: ${text.title}` : `${text.action}: ${text.title}`}
                  onClick={() => toggle(item, index + 1)}
                >
                  {isOpen ? "Close" : text.action}
                </Button>
              </div>
              {/* Always in the page, so the button's aria-controls points at it (review F1.1). The
                  panel's content, and its reads, come only while the row is open. */}
              <section id={panelId} aria-labelledby={titleId} hidden={!isOpen} className="border-t border-line bg-surface px-5 py-4">
                {isOpen ? PANELS[item.kind]({ businessId, timezone, item, hold: setHeld }) : null}
              </section>
            </li>
          );
        })}
      </ol>
    </section>
    </>
  );
}
